import { createApi } from '@reduxjs/toolkit/query/react';
import customBaseQuery from '@/lib/customBaseQuery';

const usersBaseUrl = '/api/users';
export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: customBaseQuery,
  tagTypes: ['User', 'UserList'],
  endpoints: (builder) => ({
    allUsers: builder.query<UsersTableResponse, UserSearchParams>({
      query: (params) => ({
        url: usersBaseUrl,
        params,
      }),
      providesTags: ['UserList'],
    }),
    userById: builder.query<AccountView, number | undefined>({
      query: (id) => ({
        url: `${usersBaseUrl}/${id}`,
      }),
      providesTags: (_result, _error, id) => (id ? [{ type: 'User', id }] : []),
    }),
    userByToken: builder.query<AccountView, string | null>({
      query: (token) => ({
        url: `${usersBaseUrl}/user-info?access_token=${token}`,
      }),
    }),
    requestAccess: builder.mutation<AccountView, PostUserPayload>({
      query: (payload) => ({
        url: `${usersBaseUrl}/request-access`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['UserList'],
    }),
    saveUser: builder.mutation<PortalUserData, PostUserPayload>({
      query: (payload) => ({
        url: payload.id ? `${usersBaseUrl}/${payload.id}` : `${usersBaseUrl}/new`,
        method: payload.id ? 'PUT' : 'POST',
        body: payload,
        params: payload.accessToken ? { access_token: payload.accessToken } : {},
      }),
      invalidatesTags: (_result, _error, payload) => [
        'UserList',
        ...(payload.id ? [{ type: 'User' as const, id: payload.id }] : []),
      ],
    }),
    verifyAccount: builder.mutation<string, { token: string; uid: number }>({
      query: ({ token, uid }) => ({
        url: `${usersBaseUrl}/verify-account?access_token=${token}&uid=${uid}`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { uid }) => [{ type: 'User', id: uid }],
    }),
    verifyUserName: builder.mutation<{ data: boolean }, string>({
      query: (username) => ({
        url: `${usersBaseUrl}/verify-username/${username}`,
        method: 'GET',
      }),
    }),
    getUsernames: builder.mutation<AccountView[], string>({
      query: (email) => ({
        url: `${usersBaseUrl}/get-usernames/${email}`,
        method: 'GET',
      }),
    }),
    deleteUser: builder.mutation<void, number>({
      query: (id) => ({
        url: `${usersBaseUrl}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => ['UserList', { type: 'User', id }],
    }),
  }),
});

export const {
  useAllUsersQuery,
  useGetUsernamesMutation,
  useRequestAccessMutation,
  useUserByIdQuery,
  useUserByTokenQuery,
  useSaveUserMutation,
  useVerifyAccountMutation,
  useVerifyUserNameMutation,
  useDeleteUserMutation,
} = usersApi;
